import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Grid } from '@material-ui/core';
import { companyFixedNamesMap } from '../Filters';
import { accFetchConnector, FetchAccAnalyticsPropsFromRedux } from '../accDataFetcher';
import { PNLAnalyticsEndpoint, PNLFetchStatus } from '../../actions/pnlAnalytics';
import { PNLGoodiesNonCompletedResponses } from '../../pnlAnalytics/PaperPlaceHolder';
import { AccountingDanaosVessels } from '../../pnlAnalytics/fetchTypes';
import ExpandableTable, { ExpandableTableConfig } from '../../tables/ExpandableTable';
import { UniqueTableName } from '../../common/uniqueTableName';
import { SimpleTableColumnConfig, SimpleTableRow, TableSize } from '../../tables/types';
import { AggFuncName } from '../../common/cellRangeAgg';
import { exposuresNumFormatter } from '../../common/exposures';
import { kFormatter, kFormatterWithParenthesis } from '../../containers/pivotHelper';
import { MIN_12_12_EL_WIDTH } from '../../common/common';
import { makeStyles } from '@material-ui/core/styles';
import { shallowEqual, useSelector } from 'react-redux';
import { RootReducerStateType } from '../../reducers';
import { SetFiltersPayloadType } from '../../actions/accounting';
import { CostCellComp } from './CostCell';

const useStyles = makeStyles(() => ({
    balanceSheetColumn: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: '350px !important',
        fontSize: '15px !important',
    },
    cardColumn: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: '400px !important',
        fontSize: '15px !important',
    },
    balanceSheetColumnAllCompanies: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: '200px !important', // 350
        fontSize: '15px !important',
    },
    cardColumnAllCompanies: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: '250px !important', // 400
        fontSize: '15px !important',
    },
    totalColumn: {
        borderLeft: '#bfbfbf solid 1px',
    },
}));

const MINOR_ADMIN_COMPANIES = ['Sariano', 'Shareholders', 'SwissChemGas Holding'];

type Props = FetchAccAnalyticsPropsFromRedux &
    RouteComponentProps & {
        // minHeight: string;
        // allColumnNamesKey: string;
        // uniqueTableName: UniqueTableName;
        // title: string;
        // tableDataKey: string;
    };

const GRAND_TOTAL_ROW = 'Total Administrative Cost';

export const staticColumns: Array<SimpleTableColumnConfig> = [
    {
        name: 'bsg_code',
        title: 'Balance Sheet Group',
        bodyTemplate2: (val, row) => {
            if (val === GRAND_TOTAL_ROW) return val;
            return `[${val}] ${row['name']}`;
        },
        hoverElement: (row, _) => {
            let ledger_cards = row['missing_ledger_cards'];
            if (!ledger_cards) return '';
            return (
                <div>
                    <p>The bellow ledger cards where not shown as they resulted in 0 values</p>
                    <p>{ledger_cards}</p>
                </div>
            );
        },
    },
    {
        name: 'ledger_card_code',
        title: 'Ledger Card',
        bodyTemplate2: (val, row) => {
            if (!val) return val;
            return `[${val}] ${row['name']}`;
        },
    },
];

const AdminCostComp = (props: Props) => {
    const classes = useStyles();
    const danaosVesselsData = props.pnlAnalytics.accounting_danaos_vessels;

    const filters = useSelector<RootReducerStateType, SetFiltersPayloadType>((state) => state.accounting, shallowEqual);

    if (danaosVesselsData.status !== PNLFetchStatus.COMPLETED) {
        return (
            <PNLGoodiesNonCompletedResponses
                endpoint={PNLAnalyticsEndpoint.AccountingDanaosVessels}
                data={danaosVesselsData}
                minHeight={'500px'}
            />
        );
    }

    // Construct the config columns
    let respData = danaosVesselsData.data! as AccountingDanaosVessels;

    let data = respData.admin_cost;

    let columns: Array<string> = respData.cost_sorted_admin_names.concat(['Total']);
    if (filters.hideMinorAdminCompanies) {
        columns = columns.filter(function (admin_name) {
            return MINOR_ADMIN_COMPANIES.indexOf(admin_name) < 0;
        });
        staticColumns[0].headerCellClassName = classes.balanceSheetColumn;
        staticColumns[1].headerCellClassName = classes.cardColumn;
    } else {
        staticColumns[0].headerCellClassName = classes.balanceSheetColumnAllCompanies;
        staticColumns[1].headerCellClassName = classes.cardColumnAllCompanies;
    }
    let compColumnsConfig: Array<SimpleTableColumnConfig> = columns.map((columnName) => {
        return {
            name: `${columnName}|amount_report`,
            title: (companyFixedNamesMap.get(columnName)?.title as string) || columnName,
            customDisplay: (row: SimpleTableRow, rawVal: any) => {
                return CostCellComp(
                    filters,
                    columnName,
                    row,
                    rawVal,
                    data,
                    '12px',
                    kFormatterWithParenthesis,
                    kFormatter
                );
            },
        };
    });

    compColumnsConfig[compColumnsConfig.length - 1].headerCellClassName = classes.totalColumn;
    compColumnsConfig[compColumnsConfig.length - 1].bodyCellsClassName = classes.totalColumn;

    const config: ExpandableTableConfig = {
        uniqueTableName: UniqueTableName.AdminRunningCost,
        title: 'Admin Cost',
        size: TableSize.Medium,
        columns: staticColumns.concat(compColumnsConfig),
        grandTotal: true,
        grandTotalRowName: GRAND_TOTAL_ROW,
        aggFormatVal: (aggFuncName: AggFuncName, val: number) => {
            return aggFuncName === AggFuncName.Count ? val : exposuresNumFormatter(val);
        },
        pivotDepth: 1,
        defaultExpandable: 0,
        stickyHeader: true,
    };

    return (
        <Grid item xs={12} style={{ backgroundColor: 'clear', marginTop: '50px', minWidth: MIN_12_12_EL_WIDTH }}>
            <ExpandableTable data={data.data} config={config} />
        </Grid>
    );
};
export default accFetchConnector(AdminCostComp);
